import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { gsap } from "gsap";

/**
  Use when an in-place loading animation is desired in response to user input.
 */

const ButtonLoading = ({
  buttonTextDefault = "Save",
  buttonTextLoading = "Saving .   . .",
  classList = "",
  disabled = false,
  displayMessage = "",
  sendClick = () => {},
  sendLoaded = () => {},
  sendTimeline = () => {}
}) => {
  const [buttonText, setButtonText] = useState(buttonTextDefault);
  const [buttonDisabled, setButtonDisabled] = useState(disabled);

  const timeline = useRef(gsap.timeline({ paused: true }));

  const wvWhite = "#ffffff";
  const wvOrange = "#ff6b00";

  let loadingButton = null;
  let loadingFill = null;
  let loadingText = null;

  const isLoading = (element) => {
    document.activeElement.blur();
    element.classList.add("is-loading");
  };

  const hasLoaded = (element) => {
    setButtonText(buttonTextDefault);
    sendLoaded();
    if (displayMessage === "success") {
      setButtonDisabled(true);
    }
    element.classList.remove("is-loading");
  };

  const handleClick = (e) => {
    setButtonText(buttonTextLoading);
    sendClick(e);
    timeline.current.seek("start");
    timeline.current.play();
  };

  /** Create the GSAP timeline on render & send the timeline to the parent element.
   *  Update the timeline with new displayMessage from parent.
   */
  useEffect(() => {
    timeline.current = gsap
      .timeline({ paused: true })
      .addLabel("start")
      .call(isLoading, [loadingButton])
      .set(loadingText, { autoAlpha: 0 })
      .to(loadingText, { duration: 0.4, autoAlpha: 1, color: wvWhite }, "start")
      .to(loadingFill, { duration: 0.4, autoAlpha: 1 }, "start")
      .to(loadingFill, { duration: 3, width: "100%" }, "start")
      .addLabel("response")
      .to(loadingText, { duration: 0.4, color: wvOrange }, "response")
      .to(loadingFill, { duration: 0.4, autoAlpha: 0 }, "response")
      .set(loadingText, { color: "inherit" })
      .call(hasLoaded, [loadingButton])
      .addLabel("end");

    sendTimeline(timeline.current);
  }, []);

  /** Set the button disabled state if 'disabled' changes from the parent */
  useEffect(() => {
    setButtonDisabled(disabled);
  }, [disabled]);

  return (
    <div className={`btn-loading__wrapper ${classList}`}>
      <button
        type="button"
        ref={(button) => {
          loadingButton = button;
        }}
        id="is-loading"
        className="btn btn-normal btn-ghost-orange btn-loading--bar"
        disabled={buttonDisabled}
        onClick={(e) => handleClick(e)}
      >
        <span
          ref={(span) => {
            loadingFill = span;
          }}
          className="btn-loading--bar__fill"
        />
        <span
          ref={(span) => {
            loadingText = span;
          }}
          className="btn-loading--bar__text"
        >
          {buttonText}
        </span>
      </button>
    </div>
  );
};

ButtonLoading.propTypes = {
  /** Pass the button labels for the default and loading states. */
  buttonTextDefault: PropTypes.string,
  buttonTextLoading: PropTypes.string,
  classList: PropTypes.string,
  /** If the action after load (ie. saving a draft) returns a success, disable the button to indicate there is no further action required. This should revert to false should a user make further changes prior to submitting the form. */
  disabled: PropTypes.bool,
  /** This prop determines which message to display. Options are "", "success", or "failure". */
  displayMessage: PropTypes.string,
  /** This function sends a click event back to the parent element. */
  sendClick: PropTypes.func,
  /** This function notifies the parent element that the timeline has finished running. */
  sendLoaded: PropTypes.func,
  /** This function sends the timeline back up to the parent so that in can be manipulated accordingly. */
  sendTimeline: PropTypes.func
};

export default ButtonLoading;
